import { Controller, Form } from 'react-hook-form';
import { isNil } from 'lodash';
import { Box, Button, TextField } from '@mui/material';
import { t } from 'i18next';
import AddIcon from '@mui/icons-material/Add';

import capitalizeFirstLetter from '@util/first-letter-capitalize';
import { Select } from 'ui';
import { theme } from '@store';
import { CustomModal, SaveChangesModal } from '@components';

import { useCreateUser } from './hooks';

export const CreateUser = ({
  handleOpenUserDetails,
}: {
  handleOpenUserDetails: (id: string) => void;
}) => {
  const {
    control,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    roles,
    titles,
    recordType,
    dynamicEntityNameOptions,
    showTeamDropdown,
    teamsShortOptions,
    suffixOptions,
    supervisorOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCreateUser(handleOpenUserDetails);

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color='secondary'
        variant='contained'
        onClick={handleOnClick}
      >
        {t('adminPanel.users.addModal.addOrganization')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('adminPanel.users.addModal.title')}
        isLoading={isSubmitting}
      >
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='firstName'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  size='medium'
                  label={t('adminPanel.users.addModal.firstName')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                  required
                />
              )}
            />
            <Controller
              name='lastName'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  size='medium'
                  label={t('adminPanel.users.addModal.lastName')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                  required
                />
              )}
            />
          </Box>

          <Controller
            name='suffix'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                size='medium'
                label={t('adminPanel.users.addModal.optionalSuffix')}
                error={!isNil(error)}
                options={suffixOptions}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name='title'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                size='medium'
                label={t('adminPanel.users.addModal.titleLabel')}
                error={!isNil(error)}
                options={titles}
                helperText={error?.message}
                clearable
              />
            )}
          />

          <Controller
            name='email'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                size='medium'
                label={t('adminPanel.users.addModal.emailAddress')}
                variant='outlined'
                error={!isNil(error)}
                helperText={
                  error
                    ? error.message
                    : 'Use corporate email address e.g. @brigadehealth.com or @seniordoc.com'
                }
                fullWidth
                required
              />
            )}
          />

          <Controller
            name='role'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                size='medium'
                label={t('adminPanel.users.addModal.role')}
                error={!isNil(error)}
                required
                options={roles}
                helperText={error?.message}
              />
            )}
          />

          {recordType && (
            <Controller
              name='recordId'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={`${capitalizeFirstLetter(recordType)}`}
                  error={!isNil(error)}
                  required
                  options={dynamicEntityNameOptions().map(o => ({
                    value: o.id,
                    label: o.name,
                  }))}
                  helperText={error?.message}
                />
              )}
            />
          )}

          {showTeamDropdown && (
            <Controller
              name='teamId'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.users.addModal.team')}
                  error={!isNil(error)}
                  options={teamsShortOptions}
                  helperText={error?.message}
                />
              )}
            />
          )}

          {showTeamDropdown && (
            <Controller
              name='supervisor_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  options={supervisorOptions}
                  name='supervisor_id'
                  label='Supervisor (optional)'
                  size='medium'
                  error={!isNil(error)}
                  helperText={error?.message}
                  clearable
                  clearLabel='No supervisor'
                />
              )}
            />
          )}

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              position: 'sticky',
              bottom: 0,
              paddingY: 3,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Button
              variant='text'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
              }}
              disabled={isSubmitting}
              onClick={handleClose}
            >
              {t('general.cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={isSubmitting || !isValid}
            >
              {t('general.ADD')}
            </Button>
          </Box>
        </Form>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
